import React, { useState, useEffect } from 'react';
import {
  Button,
  Container,
  Row,
  Col,
  Alert,
  Card,
  Spinner,
} from 'react-bootstrap';
import SideMenu from '../components/common/SideMenu';
import AdminMenuButton from '../components/common/AdminMenuButton';
import './Common.scss';
import contactService from '../services/contactService';
import { Contact } from '../models/contact';
import ContactTable from '../components/contact/ContactTable';

const Users: React.FC = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true); // 初期状態をtrueに変更
  const [isSideMenuOpen, setIsSideMenuOpen] = useState<boolean>(true);

  const handleSideMenuToggle = (isOpen: boolean): void => {
    setIsSideMenuOpen(isOpen);
  };

  // メッセージを自動で消去するヘルパー関数
  const clearMessagesAfterDelay = (): void => {
    setTimeout(() => {
      setError('');
      setSuccess('');
    }, 5000); // 5秒後に自動消去
  };

  // お問い合わせ一覧を取得する関数
  const fetchContacts = async (showLoader: boolean = false): Promise<void> => {
    if (showLoader) setLoading(true);
    try {
      const response = await contactService.getContacts();
      setContacts(response);
      setError('');
    } catch (err) {
      console.error('お問い合わせ一覧取得エラー:', err);
      setError('お問い合わせ一覧の取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  // ページ初期表示時にお問い合わせ一覧を取得
  useEffect(() => {
    fetchContacts();
  }, []);

  // 手動でリフレッシュするためのボタン用関数
  const handleManualRefresh = (): void => {
    fetchContacts(true);
  };

  const handleUpdateContact = async (
    updatedContact: Contact
  ): Promise<void> => {
    try {
      await contactService.updateContact(updatedContact.id, updatedContact);
      // 更新成功時にお問い合わせ一覧を再取得
      await fetchContacts();
      setError(''); // エラーメッセージをクリア
      setSuccess(
        `お問い合わせ「${updatedContact.name}」の情報を更新しました。`
      );
      clearMessagesAfterDelay();
    } catch (error) {
      console.error('お問い合わせ更新失敗', error);
      setError('お問い合わせの更新に失敗しました。');
      setSuccess('');
      clearMessagesAfterDelay();
    }
  };

  const handleDeleteContact = async (contactId: number): Promise<void> => {
    const contactToDelete = contacts.find(contact => contact.id === contactId);
    try {
      await contactService.deleteContact(contactId);
      // 削除成功時にお問い合わせ一覧を再取得
      await fetchContacts();
      setError(''); // エラーメッセージをクリア
      setSuccess(
        `お問い合わせ「${contactToDelete?.name || 'ID:' + contactId}」を削除しました。`
      );
      clearMessagesAfterDelay();
    } catch (error) {
      console.error('お問い合わせ削除失敗', error);
      setError('お問い合わせの削除に失敗しました。');
      setSuccess('');
      clearMessagesAfterDelay();
    }
  };

  return (
    <div className="page-layout">
      <AdminMenuButton
        onToggle={handleSideMenuToggle}
        isOpen={isSideMenuOpen}
      />

      <Container fluid>
        <Row className="min-vh-100 g-0">
          {/* サイドバー: 条件付き表示 */}
          {isSideMenuOpen && (
            <Col xs={12} md={3} className="admin-sidebar-col">
              <div className="p-3 position-sticky" style={{ top: '1rem' }}>
                <SideMenu />
              </div>
            </Col>
          )}

          {/* メインコンテンツ: サイドバーの状態に応じて幅を調整 */}
          <Col
            xs={12}
            md={isSideMenuOpen ? 9 : 12}
            className="py-4 admin-main-content"
          >
            <div
              className="px-3"
              style={{ marginLeft: isSideMenuOpen ? '0' : '70px' }}
            >
              <h1 className="mb-4">お問い合わせ管理</h1>

              <Card className="shadow-sm">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <Button
                      variant="primary"
                      onClick={handleManualRefresh}
                      disabled={loading}
                      className="d-flex align-items-center"
                    >
                      {loading && (
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          className="me-2"
                        />
                      )}
                      <i className="fas fa-sync-alt me-2"></i>
                      {loading ? 'データ取得中...' : 'お問い合わせ一覧更新'}
                    </Button>
                    <div className="text-muted d-flex align-items-center">
                      {loading && contacts.length > 0 && (
                        <div className="me-3 d-flex align-items-center">
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            className="me-2"
                          />
                          <small>更新中...</small>
                        </div>
                      )}
                      {contacts.length > 0 && (
                        <>
                          <i className="fas fa-users me-2"></i>
                          <span>{contacts.length} 件のお問い合わせ</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* 成功メッセージ */}
                  {success && (
                    <Alert
                      variant="success"
                      className="mb-3"
                      dismissible
                      onClose={() => setSuccess('')}
                    >
                      <Alert.Heading>
                        <i className="fas fa-check-circle me-2"></i>
                        操作が完了しました
                      </Alert.Heading>
                      {success}
                    </Alert>
                  )}

                  {/* エラーメッセージ */}
                  {error && (
                    <Alert
                      variant="danger"
                      className="mb-3"
                      dismissible
                      onClose={() => setError('')}
                    >
                      <Alert.Heading>
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        エラーが発生しました
                      </Alert.Heading>
                      {error}
                    </Alert>
                  )}

                  {/* 初期ローディング時の表示 */}
                  {loading && contacts.length === 0 ? (
                    <div className="text-center py-5">
                      <Spinner
                        animation="border"
                        variant="primary"
                        className="mb-3"
                      />
                      <h5 className="text-muted">
                        お問い合わせデータを取得中...
                      </h5>
                      <p className="text-muted">しばらくお待ちください。</p>
                    </div>
                  ) : (
                    <ContactTable
                      contacts={contacts}
                      onUpdateContact={handleUpdateContact}
                      onDeleteContact={handleDeleteContact}
                    />
                  )}
                </Card.Body>
              </Card>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Users;
